<template>
  <div class="v-dt">
    <DataTable
        v-model:selection="selectionRef"
        :value="pagedData"
        v-bind="mergedOptions"
        @rowSelect="handleRowSelect"
        @rowClick="handleRowClick"
        @rowDblclick="handleRowDblclick"
        @sort="handleSort"
        ref="vDtRef"
    >
      <template #header>
        <div class="d-flex flex-row v-dt-header">
          <div class="flex-1-1">
            <slot name="header-left"/>
          </div>
          <div v-if="mergedOptions.hideGlobalFilter !== true">
            <input
                class="form-control form-control-sm"
                type="text"
                v-model="globalFilterRef"
                placeholder="검색..."
            />
          </div>
          <div v-if="Array.isArray(props.exportOptions) && props.exportOptions.length > 0" class="ml-2">
            <div class="dropdown">
              <button
                  class="btn btn-outline-secondary btn-sm btn-header"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  title="데이터를 내보내기"
              >
                <BIconBoxArrowUp/>
              </button>
              <div class="dropdown-menu">
                <label
                    v-for="(opt, idx) in props.exportOptions" :key="opt.name"
                    class="dropdown-item"
                    @click="handleExportData(idx)"
                >
                  {{ opt.name }}
                </label>
              </div>
            </div>
          </div>
          <div v-if="slots.visibilityCtrl">
            <div class="ml-2">
              <ConfirmPopup :group="compId">
                <template #message>
                  <slot
                      name="visibilityCtrl"
                      :closePopup="() => confirm.close()"
                  />
                </template>
              </ConfirmPopup>
              <button
                  class="btn btn-outline-secondary btn-sm btn-header dropdown-toggle"
                  @click="openVisibilityPopup($event)"
              >
                조회항목
              </button>
            </div>
          </div>
          <slot name="header-right"></slot>
        </div>
      </template>

      <template #empty>
        데이터가 없습니다.
      </template>

      <Column
          v-if="props.showCheckboxSelection"
          selectionMode="multiple"
          :headerClass="'checkbox-th'"
      />

      <Column
          v-for="col in props.columns"
          :key="col.field"
          :field="col.field"
          :sortField="col.sortField"
          :header="col.header"
          :footer="col.footer"
          :sortable="typeof col.sortable === 'boolean' ? col.sortable : true"
          :bodyClass="col.bodyClass"
          :headerClass="col.headerClass"
          :style="col.style"
      >
        <template #header v-if="col.headerSlot">
          <slot :name="col.headerSlot"/>
        </template>

        <template #body="{ data, field }" v-if="col.bodySlot">
          <slot
              :name="col.bodySlot"
              :data="data"
              :field="field"
          />
        </template>
      </Column>
    </DataTable>
    <div class="footer-wrap" v-if="paginatorRef">
      <VueDataTablePaginator
          :first="firstIdxOnDataShownRef"
          :rows="rowsRef"
          :totalRecords="tableDataRef.shownCt"
          :rowsPerPageOptions="rowsPerPageOptionsRef"
          @firstChange="firstIdxOnDataShownRef = $event"
      />
      <div>
        <select class="form-select" v-model="rowsRef">
          <option v-for="rowsPerPageOpt in rowsPerPageOptionsRef" :key="rowsPerPageOpt" :value="rowsPerPageOpt">
            {{ rowsPerPageOpt }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineExpose, defineProps, nextTick, ref, useSlots, watch} from "vue";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {BIconBoxArrowUp} from "bootstrap-icons-vue";
import xlsx from "xlsx-js-style";
import {isFalsy} from "@/components/tacho/common/CommonFunc";
import ConfirmPopup from "primevue/confirmpopup";
import {useConfirm} from "primevue/useconfirm";
import {useStore} from "vuex";
import VueDataTablePaginator from "@/components/tacho/component/VueDataTablePaginator.vue";

const sortMultipleFieldsCollator = new Intl.Collator(undefined, {
  numeric: true, // "10" > "9" instead of "1" > "9"
});
const store = useStore();
const setLoading = (value) => store.commit('setLoading', value)
const compId = `${(new Date()).getTime()}-${Math.floor(Math.random() * 1000)}`;
const confirm = useConfirm();
const slots = useSlots();
const emit = defineEmits(['rowSelect', 'rowClick', 'rowDblclick', 'changeSelection']);
const props = defineProps({
  columns: Array,
  data: Array,
  options: Object,
  exportOptions: {
    type: Object,
    default: null,
  },
  rowClass: {
    type: Function,
    required: false,
  },
  showCheckboxSelection: {
    type: Boolean,
    default: false,
  }
});
const sortDataRef = ref([]);
const paginatorRef = ref(true);
const firstIdxOnDataShownRef = ref(0);
const rowsPerPageOptionsRef = ref([5, 15, 50, 100]);
const rowsRef = ref(15);
const selectionRef = ref(null);
const mergedOptions = ref(getMergedOpts());
const vDtRef = ref(null);
const globalFilterRef = ref('');

const tableDataRef = ref(initTableData(props.data))
const pagedData = ref(getPagedData());

function initTableData(newData) {
  const tData = {
    shownCt: newData.length,
    rowStates: newData.map((item) => {
      return {
        item,
        isShown: true,
      }
    }),
  }

  applySortData(tData, sortDataRef.value);
  applyFilterData(tData);
  firstIdxOnDataShownRef.value = 0;
  return tData;
}

function applySortData(tmpTableData, sortData) {
  let a, b;
  tmpTableData.rowStates.sort((aRowState, bRowState) => {
    a = aRowState.item;
    b = bRowState.item;

    for (const {field, order} of sortData) {
      const valA = _.get(a, field);
      const valB = _.get(b, field);

      // handle nulls
      if (valA == null && valB == null) continue;
      if (valA == null) return 1 * order;
      if (valB == null) return -1 * order;

      // handle dates
      if (valA instanceof Date && valB instanceof Date) {
        const diff = valA.getTime() - valB.getTime();
        if (diff !== 0) {
          return diff * order;
        }
        continue;
      }

      // handle numbers
      if (typeof valA === 'number' && typeof valB === 'number') {
        const diff = valA - valB;
        if (diff !== 0) {
          return diff * order;
        }
        continue;
      }

      // handle strings
      const diff = sortMultipleFieldsCollator.compare(String(valA), String(valB));
      if (diff !== 0) {
        return diff * order;
      }
    }
    return 0;
  });
}

function applyFilterData(tmpTableData) {
  const filterVal = globalFilterRef.value.trim().toLowerCase();
  const displayColumnFields = props.columns.map((colData) => {
    return colData.field;
  });

  const checkFunc = filterVal.length === 0
      ? () => true
      : (item) => {
        return displayColumnFields.some((field) => {
          const v = _.get(item, field);
          if (isFalsy(v)) {
            return false;
          }
          return String(v).toLowerCase().includes(filterVal);
        });
      };

  let shownCt = 0;
  tmpTableData.rowStates.forEach((rowState) => {
    rowState.isShown = checkFunc(rowState.item);
    if (rowState.isShown) {
      shownCt++;
    }
  });
  tmpTableData.shownCt = shownCt;
}

function getTableShownItems() {
  const items = [];
  for (let i = 0; i < tableDataRef.value.rowStates.length; i++) {
    if (tableDataRef.value.rowStates[i].isShown) {
      items.push(tableDataRef.value.rowStates[i].item);
    }
  }

  return items;
}

function getPagedData() {
  const items = getTableShownItems();

  if (paginatorRef.value === false) {
    return items;
  }

  return items.slice(firstIdxOnDataShownRef.value, firstIdxOnDataShownRef.value + rowsRef.value);
}

function cloneTableData() {
  return {
    ...tableDataRef.value,
    rowStates: [...tableDataRef.value.rowStates],
  };
}

watch(
    sortDataRef,
    () => {
      const nextTableData = cloneTableData();
      applySortData(nextTableData, sortDataRef.value);
      tableDataRef.value = nextTableData;
    }
)

watch(
    globalFilterRef,
    () => {
      const nextTableData = cloneTableData();
      applyFilterData(nextTableData);
      tableDataRef.value = nextTableData;
      firstIdxOnDataShownRef.value = 0;
    }
)

watch(
    [
      tableDataRef,
      firstIdxOnDataShownRef,
      rowsRef,
    ], () => {
      pagedData.value = getPagedData();
    }
)

function handleSort(evt) {
  if (typeof evt !== 'object') {
    return;
  }

  if (evt.sortMode === 'multiple') {
    if (Array.isArray(evt.multiSortMeta) && evt.multiSortMeta.length > 0) {
      sortDataRef.value = evt.multiSortMeta.map((v) => {
        return {
          field: v.field,
          order: v.order,
        }
      });
    }
    return;
  }

  sortDataRef.value = [
    {
      field: evt.sortField,
      order: evt.sortOrder,
    },
  ];
}

function convertSortToJquerySort() {
  if (sortDataRef.value.length === 0) {
    return null;
  }

  return {
    column: sortDataRef.value[0].field,
    order: sortDataRef.value[0].order === 1 ? 'asc' : 'desc',
  }
}

function getMergedOpts() {
  const opts = {
    paginator: true,
    hideGlobalFilter: false,
    rowHover: false,
    metaKeySelection: false,
    ...props.options || {},
  };

  if (typeof opts.rows !== 'undefined') {
    rowsRef.value = opts.rows;
    delete opts.rows;
  }

  if (typeof opts.rowsPerPageOptions !== 'undefined') {
    rowsPerPageOptionsRef.value = opts.rowsPerPageOptions;
    delete opts.rowsPerPageOptions;
  }

  if (rowsPerPageOptionsRef.value.indexOf(rowsRef.value) === -1) {
    rowsPerPageOptionsRef.value = [...rowsPerPageOptionsRef.value, rowsRef.value].sort((a, b) => a - b);
  }

  if (opts.sortField) {
    sortDataRef.value = [
      {
        field: opts.sortField,
        order: typeof opts.sortOrder === 'number' ? opts.sortOrder : 1,
      },
    ];
  }

  if (typeof opts.paginator === 'boolean') {
    paginatorRef.value = opts.paginator;
    opts.paginator = false;
    opts.rowsPerPageOptions = [];
  }

  handleSort(opts);

  return opts;
}

function handleRowSelect(evt) {
  emit('rowSelect', {key: evt.data.key, data: evt.data});
}

function handleRowClick(evt) {
  emit('rowClick', evt);
}

function handleRowDblclick(evt) {
  emit('rowDblclick', evt);
}

function getNeighborsByKey(key) {
  const keyIdx = tableDataRef.value.rowStates.findIndex((rowState) => {
    return rowState.item.key === key;
  });

  if (keyIdx === -1) {
    return {
      prev: null,
      next: null,
    };
  }

  return {
    prev: keyIdx > 0 ? tableDataRef.value.rowStates[keyIdx - 1].item : null,
    next: keyIdx < tableDataRef.value.rowStates.length - 1 ? tableDataRef.value.rowStates[keyIdx + 1].item : null,
  };
}

function cleanSelection() {
  selectionRef.value = null;
}

function focusPageByItem(item) {
  const idx = getTableShownItems().indexOf(item);
  if (idx === -1) {
    return;
  }

  const page = Math.floor(idx / rowsRef.value);
  firstIdxOnDataShownRef.value = page * rowsRef.value;
  const el = vDtRef.value.$el.querySelector('tr.p-highlight');
  el?.scrollIntoView({block: 'center'});
}

function setSelection(item) {
  selectionRef.value = item;

  if (paginatorRef.value === false) {
    return;
  }

  nextTick(() => {
    focusPageByItem(item);
  });
}

async function handleExportData(idx) {
  const exportData = props.exportOptions[idx];
  if (!exportData) {
    return;
  }

  if (typeof exportData.exportFn === 'function') {
    setLoading(true);
    try {
      await exportData.exportFn({
        jqueryOrderData: convertSortToJquerySort(sortDataRef.value),
      })
    } catch (err) {
      console.error('Error occurred during export:', err);
    } finally {
      setLoading(false);
    }
    return;
  }

  const excelDataList = [];
  const validExportCols = exportData.columns.filter(({excelCfg}) => {
    if (!excelCfg) {
      console.warn('Excel export config is missing for column:', excelCfg);
      return false;
    }
    return true;
  });

  // Header
  const headerStyle = {
    fill: {fgColor: {rgb: "FFFF00"}},
    alignment: {horizontal: "center"},
    border: {top: {style: "thin"}, bottom: {style: "thin"}, left: {style: "thin"}, right: {style: "thin"}},
    font: {bold: true}
  };
  excelDataList.push(
      validExportCols.map(({header}) => {
        return {v: header, t: "s", s: headerStyle};
      })
  );

  // Rows
  const rowStyle = {
    alignment: {horizontal: "center"},
    border: {top: {style: "thin"}, bottom: {style: "thin"}, left: {style: "thin"}, right: {style: "thin"}}
  };
  const inactiveRowStyle = {
    ...rowStyle,
    fill: {fgColor: {rgb: "E9E9E9"}},
  };
  let v;
  props.data.forEach((item) => {
    const rowData = [];
    validExportCols.forEach(({field, excelCfg}) => {
      v = excelCfg.adaptAggValue ? excelCfg.adaptAggValue(_.get(item, field)) : _.get(item, field);
      rowData.push({
        v: isFalsy(v) ? '' : v,
        s: isFalsy(v) ? inactiveRowStyle : rowStyle,
        ...isFalsy(v) ? {} : excelCfg.cellProps,
      });
    });
    excelDataList.push(rowData)
  });

  const ws = xlsx.utils.aoa_to_sheet(excelDataList);
  ws["!cols"] = validExportCols.map(({excelCfg}) => {
    return {width: excelCfg.width};
  });
  ws['!margins'] = {
    left: 0.2,    // Left margin
    right: 0.2,   // Right margin
    top: 0.2,     // Top margin
    bottom: 0.2,  // Bottom margin
    header: 0.2,  // Header margin
    footer: 0.2   // Footer margin
  };

  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, exportData.getSheetName());
  xlsx.writeFile(wb, `${exportData.getFilename()}.xlsx`);
}

function openVisibilityPopup(event) {
  confirm.require({
    target: event.currentTarget,
    group: compId,
  });
}

function getJqueryOrderData() {
  return convertSortToJquerySort(sortDataRef.value);
}

watch(
    () => props.data,
    (newData) => {
      tableDataRef.value = initTableData(newData);
    }
)

watch(
    selectionRef,
    () => {
      emit('changeSelection', selectionRef.value)
    }
)

defineExpose({
  setSelection,
  cleanSelection,
  getNeighborsByKey,
  getJqueryOrderData,
});
</script>

<style scoped>
.v-dt-header .btn-header {
  border-color: var(--table-paging-border);
}
</style>

<style>
.v-dt {
  --table-blue: #0d6efd;
  --table-header: #e3e3e3;
  --table-paging-border: #dee2e6;
  --table-paging-disabled: #e9ecef;
}

.v-dt .p-component {
  font-family: 'Pretendard', Arial, sans-serif;
}

.v-dt .p-datatable .p-datatable-header {
  padding: 6px 10px;
  border: 0;
}

.v-dt .p-datatable-wrapper span {
  margin: 0;
}

.v-dt .p-datatable .p-datatable-thead {
  z-index: 11;
}

.v-dt .p-datatable .p-datatable-tfoot {
  z-index: 11;
}

.v-dt .p-datatable .checkbox-th {
  width: 50px;
}

.v-dt .p-datatable .checkbox-th .p-column-header-content {
  justify-content: center;
}

.v-dt .p-datatable .p-datatable-tfoot > tr > td {
  padding: 8px 0;
  font-size: 0.9rem;
  text-align: center;
}

.v-dt .p-datatable .p-datatable-thead > tr > th {
  padding: 0;
  border-width: 0;
  background: var(--table-header);
}

.v-dt .p-datatable .p-datatable-thead .p-column-header-content {
  background: var(--table-header);
  padding: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  height: 44px;
}

.v-dt .p-datatable .p-column-title {
  flex: 1;
  text-align: center;
  color: black;
  font-weight: 500;
  font-size: 0.9rem;
}

.v-dt .p-datatable .p-sortable-column-icon {
  width: 12px;
}

.v-dt .p-datatable .p-datatable-tbody > tr {
  border-bottom: 1px solid #ddd;
}

.v-dt .p-datatable .p-datatable-tbody > tr > td {
  padding: 6px 12px;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: center;
}

.v-dt .p-datatable .p-sortable-column .p-sortable-column-icon {
  margin-left: 3px;
  color: #5b5b5b;
}

.v-dt .p-datatable .p-sortable-column.p-highlight .p-sortable-column-icon {
  color: black;
}

.footer-wrap {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 16px;
  padding-top: 12px;
}

.footer-wrap .form-select {
  padding: 5px 25px 5px 10px;
  font-size: 14px;
  height: 36px;
  background-position: right 0.35rem center
}
</style>
